import { useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Link,
  Typography
} from '@mui/material';

import { NCD_API_BASE_URL } from "../../../../apiConfig"; // Import the base URL
import Modal from '../../../../components/Modal';

const TreatmentHistory = ({treatmentData = [], loading = false, status = "" }) => {
  const [showAll, setShowAll] = useState(false);
  const [medicineModal, setMedicineModal] = useState({ isOpen: false, data: null });
  const [followUpModal, setFollowUpModal] = useState({ isOpen: false, data: null });

  const handleViewMedicines = useCallback((medicineData) => {
    setMedicineModal({ isOpen: true, data: medicineData });
  }, []);

  const handleViewFollowUp = useCallback((followUpData) => {
    setFollowUpModal({ isOpen: true, data: followUpData });
  }, []);

  const displayData = showAll ? treatmentData : treatmentData.slice(0, 2);

  return (
    <Box className="treatment-history">
      <Box className="status-bar">
        <span>Status: {status || "-"}</span>
      </Box>

      <Box className="quick-links">
        <Link href={`${NCD_API_BASE_URL}/src/app/assets/pdf/diabetes.pdf`} underline="none" target="_blank">
          🔗 Quick Links
        </Link>
      </Box>

      <Box className="history-section">
        <h3>Treatment History</h3>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : treatmentData.length === 0 ? (
          <Typography>No data available</Typography>
        ) : (
          <>
            <TableContainer component={Paper} className="history-table">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#c5cae9', '& .MuiTableCell-root': { fontWeight: 'bold' } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Facility</TableCell>
                    <TableCell>Fasting BS mg/dl</TableCell>
                    <TableCell>Post Prandial BS mg/dl</TableCell>
                    <TableCell>Random BS mg/dl</TableCell>
                    <TableCell>Diagnosis</TableCell>
                    <TableCell>Medicines</TableCell>
                    <TableCell>Follow Up</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.facility}</TableCell>
                      <TableCell>{row.fastingBS}</TableCell>
                      <TableCell>{row.postPrandialBS}</TableCell>
                      <TableCell>{row.randomBS}</TableCell>
                      <TableCell>{row.diagnosis}</TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleViewMedicines(row.medicines)}
                        >
                          View Medicines
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleViewFollowUp(row.followUp)}
                        >
                          View Follow ups
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {treatmentData.length > 2 && (
              <Box mt={2}>
                <Button 
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? 'SHOW LESS ▲' : 'SHOW MORE ▼'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Medicine Modal */}
      <Modal
        isOpen={medicineModal.isOpen}
        onClose={() => setMedicineModal({ isOpen: false, data: null })}
        title="Medicine List"
        size="large"
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#c5cae9' }}>
                <TableCell><strong>Medicine</strong></TableCell>
                <TableCell><strong>Frequency</strong></TableCell>
                <TableCell><strong>Duration (in days)</strong></TableCell>
                <TableCell><strong>Quantity</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {medicineModal.data && medicineModal.data.length > 0 ? (
                medicineModal.data.map((med, index) => (
                  <TableRow key={index}>
                    <TableCell>{med.name}</TableCell>
                    <TableCell>{med.frequency}</TableCell>
                    <TableCell>{med.duration}</TableCell>
                    <TableCell>{med.quantity}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">No medicines available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => setMedicineModal({ isOpen: false, data: null })}
          >
            Close
          </Button>
        </Box>
      </Modal>

      {/* Follow Up Modal */}
      <Modal
        isOpen={followUpModal.isOpen}
        onClose={() => setFollowUpModal({ isOpen: false, data: null })}
        title="Follow Up"
        size="large"
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#c5cae9' }}>
                <TableCell><strong>Follow-up Reason</strong></TableCell>
                <TableCell><strong>Follow-up Duration</strong></TableCell>
                <TableCell><strong>Due Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {followUpModal.data ? (
                <TableRow>
                  <TableCell>{followUpModal.data.reason}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={2} alignItems="center">
                      <span>{followUpModal.data.days || '0'} Days</span>
                      <span>{followUpModal.data.weeks || '0'} Weeks</span>
                      <span>{followUpModal.data.months || '0'} Months</span>
                    </Box>
                  </TableCell>
                  <TableCell>{followUpModal.data.dueDate}</TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">No follow-up data available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => setFollowUpModal({ isOpen: false, data: null })}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default TreatmentHistory;
